// src/services/menusServices.js
import axios from "axios";

const baseUrl = `${process.env.REACT_APP_API_URL}/api/menus`;

// Lit le token à chaque appel
const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/* =========================
   Mini Cache (5 minutes)
   ========================= */
const cache = new Map();
const TTL = 5 * 60 * 1000;
const keyOf = (name, params) => `${name}:${JSON.stringify(params || {})}`;
const getC = (k) => {
  const it = cache.get(k);
  if (!it) return null;
  if (Date.now() - it.t > TTL) {
    cache.delete(k);
    return null;
  }
  return it.v;
};
const setC = (k, v) => cache.set(k, { v, t: Date.now() });
const invalidateCache = () => cache.clear();

/* =========================
   Helpers
   ========================= */
const buildQuery = (params = {}) => {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    if (typeof v === "boolean") usp.append(k, v ? "true" : "false");
    else usp.append(k, String(v));
  });
  const qs = usp.toString();
  return qs ? `?${qs}` : "";
};

/* =========================
   Public (lecture)
   ========================= */

// Liste plate
export const getMenuFlat = async ({
  menu_key = "header",
  locale = "fr",
  include_inactive = false,
  bypassCache = false,
} = {}) => {
  const k = keyOf("getMenuFlat", { menu_key, locale, include_inactive });
  if (!bypassCache) {
    const hit = getC(k);
    if (hit) return hit;
  }
  try {
    const url =
      `${baseUrl}/getMenuFlat` +
      buildQuery({ menu_key, locale, include_inactive });
    const { data } = await axios.get(url, { headers: authHeaders() });
    const out = Array.isArray(data) ? data : [];
    setC(k, out);
    return out;
  } catch (e) {
    console.error("Erreur getMenuFlat:", e);
    return [];
  }
};

// Arbre imbriqué
export const getMenuTree = async ({
  menu_key = "header",
  locale = "fr",
  include_inactive = false,
  bypassCache = false,
} = {}) => {
  const k = keyOf("getMenuTree", { menu_key, locale, include_inactive });
  if (!bypassCache) {
    const hit = getC(k);
    if (hit) return hit;
  }
  try {
    const url =
      `${baseUrl}/getMenuTree` +
      buildQuery({ menu_key, locale, include_inactive });
    const { data } = await axios.get(url, { headers: authHeaders() });
    const out = Array.isArray(data) ? data : [];
    setC(k, out);
    return out;
  } catch (e) {
    console.error("Erreur getMenuTree:", e);
    return [];
  }
};

export const menusCache = {
  clear: () => cache.clear(),
};

// Récupère les items de menus marqués comme favoris (is_favorite = true)
// pour un menu_key / locale donnés (ex : header + fr) : idéal pour le Hero "Exemple Site Mairie".
export const getFavoriteMenus = async ({
  menu_key = "header",
  locale = "fr",
  limit = 3,
  bypassCache = false,
} = {}) => {
  const k = keyOf("getFavoriteMenus", { menu_key, locale, limit });
  if (!bypassCache) {
    const hit = getC(k);
    if (hit) return hit;
  }

  try {
    const url =
      `${baseUrl}/favorites` +
      buildQuery({
        menu_key,
        locale,
        limit,
      });

    const { data } = await axios.get(url, { headers: authHeaders() });
    const out = Array.isArray(data) ? data : [];
    setC(k, out);
    return out;
  } catch (e) {
    console.error("Erreur getFavoriteMenus:", e);
    return [];
  }
};

/* =========================
   Admin / CMS (CRUD)
   ========================= */

// Recherche / listing admin
// params: { menu_key, locale, q, parent_id, is_active, page=1, limit=50, sort, dir }
export const searchMenus = async (params = {}) => {
  try {
    const qs = buildQuery(params);
    const { data } = await axios.get(`${baseUrl}/search${qs}`, {
      headers: authHeaders(),
    });
    return {
      items: Array.isArray(data?.items) ? data.items : [],
      total: Number.isFinite(data?.total) ? data.total : 0,
      page: Number.isFinite(data?.page) ? data.page : 1,
      limit: Number.isFinite(data?.limit) ? data.limit : 50,
      totalPages: Number.isFinite(data?.totalPages) ? data.totalPages : 1,
    };
  } catch (e) {
    console.error("Erreur searchMenus:", e);
    return { items: [], total: 0, page: 1, limit: 50, totalPages: 1 };
  }
};

export const getMenuById = async (id) => {
  if (!id) return null;
  try {
    const { data } = await axios.get(`${baseUrl}/${id}`, {
      headers: authHeaders(),
    });
    return data || null;
  } catch (e) {
    console.error("Erreur getMenuById:", e);
    return null;
  }
};

// payload: { menu_key?, locale?, label*, title_attr?, page_id? | url?, target?, rel?, icon?, parent_id?, order_index?, is_active? }
export const createMenuItem = async (payload) => {
  try {
    const { data } = await axios.post(`${baseUrl}`, payload, {
      headers: { "Content-Type": "application/json", ...authHeaders() },
    });
    invalidateCache();
    return data || null;
  } catch (e) {
    console.error("Erreur createMenuItem:", e);
    return null;
  }
};

export const updateMenuItem = async (id, payload) => {
  if (!id) return null;
  try {
    const { data } = await axios.put(`${baseUrl}/${id}`, payload, {
      headers: { "Content-Type": "application/json", ...authHeaders() },
    });
    invalidateCache();
    return data || null;
  } catch (e) {
    console.error("Erreur updateMenuItem:", e);
    return null;
  }
};

export const patchMenuItem = async (id, partial) => {
  if (!id) return null;
  try {
    const { data } = await axios.patch(`${baseUrl}/${id}`, partial, {
      headers: { "Content-Type": "application/json", ...authHeaders() },
    });
    invalidateCache();
    return data || null;
  } catch (e) {
    console.error("Erreur patchMenuItem:", e);
    return null;
  }
};

// === Toggle (nom demandé par MenusAdmin) ===
export const toggleMenuItem = async (id) => {
  if (!id) return null;
  try {
    const { data } = await axios.patch(`${baseUrl}/${id}/toggle`, null, {
      headers: authHeaders(),
    });
    invalidateCache();
    return data || null;
  } catch (e) {
    console.error("Erreur toggleMenuItem:", e);
    return null;
  }
};

// (Alias rétro-compatibilité si tu avais déjà toggleMenuActive)
export const toggleMenuActive = toggleMenuItem;

export const deleteMenuItem = async (id) => {
  if (!id) return false;
  try {
    await axios.delete(`${baseUrl}/${id}`, {
      headers: authHeaders(),
    });
    invalidateCache();
    return true;
  } catch (e) {
    console.error("Erreur deleteMenuItem:", e);
    return false;
  }
};

// Réordonner en masse (endpoint PATCH /reorder côté back)
export const reorderMenuItems = async (items = []) => {
  try {
    const { data } = await axios.patch(
      `${baseUrl}/reorder`,
      { items },
      { headers: { "Content-Type": "application/json", ...authHeaders() } }
    );
    invalidateCache();
    return data?.success === true || data?.ok === true;
  } catch (e) {
    console.error("Erreur reorderMenuItems:", e);
    return false;
  }
};

// (Alias optionnel si tu l’utilisais)
export const bulkReorderMenus = reorderMenuItems;
